import React from 'react';
import { StatusTag, useIsFeatureEnabled } from '@innovyze/stylovyze';
import { Grid } from '@mui/material';
import {
	HeartBeatStatusWrapper,
	Label,
} from './ComponentStatusIndicator.styles';
import {
	FLAGS,
	HeartBeatStatusIndicators,
	ReportedHeartBeatStatus,
} from '@Types';
import { getComponentStatusIndicator } from '../utils/getComponentStatusIndicator';
import { useHeartBeatStatusChecker } from '@Hooks';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import { getValue } from '@Selectors';

enum StatusText {
	disabled = 'DISABLED',
	active = 'ACTIVE',
	error = 'ERROR',
	disconnected = 'NOT CONNECTED',
	uninitialized = 'UNINITIALIZED',
}
enum ColorType {
	positive = 'positive',
	standard = 'standard',
	neutral = 'neutral',
	warning = 'warning',
	danger = 'danger',
	new = 'new',
	medium = 'medium',
	high = 'high',
	extreme = 'extreme',
}

// High level translation of the shadow to visible icons:
//  icon representing never connected
// No shadow or empty shadow
// green
// All components report status=good AND all timestamps < 6 minutes ago
// yellow
// All components report status=good AND all timestamps < 11 minutes ago
// red
// Any component reports status other than good, or any timestamp > 11 minutes ago

const ComponentStatusIndicator = (
	props: { init?: boolean } = { init: true },
) => {
	const { certificateStatus } = useSelector(getValue).implementation;

	const { heartBeatStatus } = useHeartBeatStatusChecker();
	const isIotActivationEnabled = useIsFeatureEnabled(
		FLAGS.ENABLE_IOT_ACTIVATION,
	);

	const status: HeartBeatStatusIndicators = getComponentStatusIndicator(
		heartBeatStatus?.status?.payload?.state
			?.reported as ReportedHeartBeatStatus,
		isIotActivationEnabled ? certificateStatus : undefined,
	);

	let text: StatusText = StatusText.error;
	let color: ColorType = ColorType.danger;

	if (props.init) {
		text = StatusText.uninitialized;
	} else if (status === HeartBeatStatusIndicators.Green) {
		text = StatusText.active;
		color = ColorType.positive;
	} else if (status === HeartBeatStatusIndicators.Yellow) {
		text = StatusText.active;
		color = ColorType.warning;
	} else if (status === HeartBeatStatusIndicators.Disabled) {
		text = StatusText.disabled;
		color = ColorType.neutral;
	} else if (
		status === HeartBeatStatusIndicators.Disconnected ||
		status === undefined
	) {
		text = StatusText.disconnected;
	}

	return (
		<HeartBeatStatusWrapper>
			<Grid container spacing={1} direction="column">
				<Grid item>
					<Label htmlFor="heartBeatStatus">{t('Status')}</Label>
				</Grid>
				<Grid item>
					<StatusTag
						data-cy="heartBeatStatus"
						text={text}
						color={color}
					/>
				</Grid>
			</Grid>
		</HeartBeatStatusWrapper>
	);
};

export default ComponentStatusIndicator;
